import { api } from './api';
import { dateInputToInstant, shiftDateOnly } from './taskDates';

/** One day's three numbers, as the server answers them. */
export type DayStats = {
  day: string;
  tasksCompleted: number;
  tasksCreated: number;
  winsDone: number;
  winsTotal: number;
};

export type Baseline = 'yesterday' | 'lastWeek';

/** Label and offset together, so adding "Last month" is one entry and nothing
 *  else. The label is what the toggle renders; the days are what the hook
 *  shifts by. */
export const BASELINES = [
  { value: 'yesterday', label: 'Yesterday', days: 1 },
  { value: 'lastWeek', label: 'Last week', days: 7 },
] as const satisfies readonly { value: Baseline; label: string; days: number }[];

const entryFor = (baseline: Baseline) => BASELINES.find((entry) => entry.value === baseline)!;

export const baselineDays = (baseline: Baseline): number => entryFor(baseline).days;

/** Lower-cased, because it is read mid-sentence ("up 2 from yesterday") rather
 *  than as the button's label. */
export const baselineLabel = (baseline: Baseline): string =>
  entryFor(baseline).label.toLowerCase();

/**
 * One day's numbers.
 *
 * Three parameters for one day, and all three are needed: the wins column is a
 * calendar date and the two task columns are instants, and the server cannot
 * convert between them without knowing the reader's timezone -- which is the one
 * thing it is never told. Read fresh on every call rather than memoised at
 * mount, the discipline `dayBoundFor` spells out in useDayTasks: a dashboard
 * left open overnight would otherwise keep asking about yesterday.
 */
export function fetchDayStats(day: string, signal?: AbortSignal): Promise<DayStats> {
  const query = new URLSearchParams({
    day,
    from: dateInputToInstant(day),
    // Exclusive, and shifted through a real Date -- so a 23-hour spring-forward
    // day is 23 hours here too, without this file having to know what DST is.
    to: dateInputToInstant(shiftDateOnly(day, 1)),
  });
  return api.get<DayStats>(`/api/stats/day?${query}`, signal);
}

/* ---------------------------------------------------------------------------
   Deltas.
   --------------------------------------------------------------------------- */

/** Which way this number is meant to go. `neutral` is a real answer rather than
 *  a fallback: filing more tasks is not better than filing fewer, and painting
 *  it green would be the card asserting something it does not know. */
export type Polarity = 'up' | 'neutral';

export type Delta =
  | { kind: 'none' }
  | { kind: 'flat' }
  | { kind: 'change'; amount: number; good: boolean | null };

export function deltaFor(today: number, baseline: number | null, polarity: Polarity): Delta {
  if (baseline === null) return { kind: 'none' };
  const amount = today - baseline;
  if (amount === 0) return { kind: 'flat' };
  return {
    kind: 'change',
    amount,
    good: polarity === 'neutral' ? null : amount > 0,
  };
}

/** The arrow says which way it moved; the colour says whether that was the good
 *  way. Keeping them apart is what lets a neutral stat still show direction.
 *
 *  Typographic triangles rather than entries in icons.tsx, which is the one
 *  place in this app that rule is broken and worth saying why: a 24x24
 *  1.5-stroke glyph shrunk to 12px sits on its own baseline and needs nudging
 *  against the numeral beside it, where a character in the same run of text
 *  aligns for free. Neither has emoji presentation by default. */
export const deltaText = (delta: Delta): string =>
  delta.kind === 'none'
    ? '—'
    : delta.kind === 'flat'
      ? '±0'
      : `${delta.amount > 0 ? '▲+' : '▼'}${delta.amount}`;

export const deltaClass = (delta: Delta): string =>
  delta.kind !== 'change' || delta.good === null
    ? 'text-stone-400'
    : delta.good
      ? 'text-emerald-600'
      : 'text-rose-600';

/** What a screen reader hears, since the triangles are decorative. */
export const deltaLabel = (delta: Delta, vs: string): string =>
  delta.kind === 'none'
    ? 'no comparison'
    : delta.kind === 'flat'
      ? `unchanged from ${vs}`
      : `${delta.amount > 0 ? 'up' : 'down'} ${Math.abs(delta.amount)} from ${vs}`;

/* ---------------------------------------------------------------------------
   The remembered toggle.
   --------------------------------------------------------------------------- */

const STORAGE_KEY = 'hive.dashboard.statsBaseline';

const isBaseline = (value: string | null): value is Baseline =>
  value !== null && BASELINES.some((entry) => entry.value === value);

/** Wrapped for the reason projectView.ts gives: localStorage throws rather than
 *  no-ops when the browser blocks site data, and a lost preference must not take
 *  the dashboard down. */
export function readStoredBaseline(): Baseline {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isBaseline(stored) ? stored : 'yesterday';
  } catch {
    return 'yesterday';
  }
}

export function storeBaseline(baseline: Baseline): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, baseline);
  } catch {
    /* Preference is a nicety, not worth surfacing an error for. */
  }
}
